/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Subscription } from "rxjs";
import { AbstractObject } from "@src/app/model/abstract-object";
import { AbstractChart } from "@src/app/shared/chart/charts/abstract-chart";
import { PicklistField } from "@src/app/shared/field/PicklistField";
import { NumberField } from "@src/app/shared/field/NumberField";
import { Field } from "@src/app/shared/field/Field";
import { AppFilterNavigator, ChartSort, ChartType } from "@src/app/shared/chart/chart-analysis";
import { PieChart } from "@src/app/shared/chart/charts/pie-chart";
import { BarChart } from "@src/app/shared/chart/charts/bar-chart";
import { TrendField } from "@src/app/shared/field/TrendField";
import { TrendLine } from "@src/app/shared/chart/charts/trend-line";
import { CompareBar } from "@src/app/shared/chart/charts/compare-bar";

export class ChartHolder {
    chartType: ChartType
    private width = 0;
    appChart: AbstractChart;
    data: AbstractObject[] = [];
    refresher: AppFilterNavigator;
    showing = false;

    private picklist: PicklistField;
    private numberField: NumberField;
    private trendField: TrendField;
    private itemsChangedSub: Subscription;

    public excludeZero = false;
    public suppressSubTitle = false;
    /** Both left undefined unless the ChartParms say otherwise - see AbstractChart.analyseData */
    public showOnly: number;
    public sortBy: ChartSort;

    readonly divId = 'chart-' + Math.round(Math.random() * 1000000);

    constructor(c: ChartType) {
        this.chartType = c;
        this.setWidth();
        this.appChart = this.newChart(c);
    }

    getDivId() {
        return this.divId;
    }

    getName() {
        if (this.numberField) {
            return this.picklist.label + ' by ' + this.numberField.label + ' Chart';
        } else {
            return this.picklist.label + ' Chart';
        }
    }

    setRefresher(refresher: AppFilterNavigator) {
        this.refresher = refresher;
    }

    setupData(items: AbstractObject[]) {
        this.data = items;
        this.showChart();
    }

    switchPicklist(picklist: Field) {
        if (!(picklist instanceof PicklistField)) {
            console.warn('Expected a picklist field, not this', picklist);
        }
        this.picklist = picklist as PicklistField;
        this.resubscribeToPicklist();
        this.showChart();
    }

    setPicklist(picklist: PicklistField) {
        this.picklist = picklist;
        this.resubscribeToPicklist();
        return this;
    }

    private resubscribeToPicklist() {
        this.itemsChangedSub?.unsubscribe();
        this.itemsChangedSub = this.picklist.itemsChanged$.subscribe(() => {
            if (this.data.length > 0) {
                this.showChart();
            }
        });
    }

    destroy() {
        this.itemsChangedSub?.unsubscribe();
    }

    getPicklist() {
        return this.picklist;
    }

    // Should be numberField or Duration...
    setMetric(numberField: Field ){
        this.numberField = numberField as NumberField;
        return this;
    }

    setTrend(trendField: TrendField ){
        this.trendField = trendField;
        return this;
    }

    switchMetric(numberField: Field){
        if (!(numberField instanceof NumberField)) {
            console.warn('Expected a number field, not this', numberField);
        }
        this.numberField = numberField as NumberField;
        this.showChart();
    }

    getMetric() : NumberField {
        return this.numberField;
    }

    private showChart() {
        const showData = this.picklist.picklist.items.length === 0 ? false : true

        this.showing = true;
        this.appChart.setupData({
            data: showData ? this.data : [],
            divId: this.divId,
            picklist: this.picklist,
            numberField: this.numberField,
            trendField: this.trendField,
            refresher: this.refresher,
            excludeZero: this.excludeZero,
            suppressSubTitle: this.suppressSubTitle,
            showOnly: this.showOnly,
            sortBy: this.sortBy
        });
    }

    setWidth() {
        if (this.chartType === 'bar' || this.chartType === 'trend' || this.chartType === 'compareBar') {
            this.width = 2;
        } else {
            this.width = 1;
        }
    }

    getWidth() {
        return this.width;
    }

    newChart(type: ChartType) {
        if (type === 'pie' ) {
            return new PieChart();
        } else if (type === 'bar') {
            return new BarChart();
        } else if (type === 'trend') {
            return new TrendLine();
        } else if (type === 'compareBar') {
            return new CompareBar();
        } else {
            console.error('Well I dunno how to make a ', type);
        }
    }
}
